<script setup lang="ts">
import { Roles } from '@/constants'
import productsHttpService from '@/services/http/productsHttpService'
import type { ProductDetailModel } from '@/services/http/httpResponses'
import { ref } from 'vue'

definePage({
  meta: { allowedRoles: [Roles.Admin] },
})

const PRODUCT_CODE = 'CGT'

const details = ref<ProductDetailModel[]>([])

const showDialog = ref(false)
const saving = ref(false)
const form = ref<{ price: number | null; cost: number | null; dateBegin: string }>({
  price: null,
  cost: null,
  dateBegin: '',
})

const load = async () => {
  const res = await productsHttpService.getProductDetails(PRODUCT_CODE)
  details.value = res.details
}

const openDialog = () => {
  const today = new Date().toISOString().slice(0, 10)
  form.value = { price: null, cost: null, dateBegin: today }
  showDialog.value = true
}

const save = async () => {
  if (form.value.price == null || form.value.price <= 0 || !form.value.dateBegin) return
  saving.value = true
  try {
    await productsHttpService.setProductDetails(PRODUCT_CODE, {
      price: form.value.price!,
      cost: form.value.cost ?? 0,
      dateBegin: form.value.dateBegin + 'T00:00:00Z',
    })
    await load()
    showDialog.value = false
  } finally {
    saving.value = false
  }
}

const rowStatus = (d: ProductDetailModel) => {
  const now = new Date()
  const begin = new Date(d.dateBegin)
  const end = d.dateEnd ? new Date(d.dateEnd) : null
  if (end != null && end <= now) return { label: 'Архив', severity: 'secondary' as const }
  if (begin > now) return { label: 'Запланирован', severity: 'info' as const }
  return { label: 'Активный', severity: 'success' as const }
}

await load()
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <Toast />
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-semibold">История тарифов</h2>
      <Button label="Установить новый тариф" icon="pi pi-plus" @click="openDialog" />
    </div>

    <DataTable :value="details" dataKey="id">
      <Column field="productName" header="Услуга" />
      <Column header="Статус">
        <template #body="slotProps">
          <Tag
            :value="rowStatus(slotProps.data).label"
            :severity="rowStatus(slotProps.data).severity"
          />
        </template>
      </Column>
      <Column header="Цена (₸)">
        <template #body="slotProps">
          {{ slotProps.data.price.toLocaleString('ru-RU') }} ₸
        </template>
      </Column>
      <Column header="Себестоимость (₸)">
        <template #body="slotProps"> {{ slotProps.data.cost.toLocaleString('ru-RU') }} ₸ </template>
      </Column>
      <Column header="Действует с">
        <template #body="slotProps">
          {{ new Date(slotProps.data.dateBegin).toLocaleDateString('ru-RU') }}
        </template>
      </Column>
      <Column header="Действует по">
        <template #body="slotProps">
          {{
            slotProps.data.dateEnd
              ? new Date(slotProps.data.dateEnd).toLocaleDateString('ru-RU')
              : '—'
          }}
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col gap-4 items-center py-4">
          <span class="pi pi-tag text-6xl text-gray-300" />
          <p class="text-gray-500">Тарифов нет</p>
        </div>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="showDialog"
      header="Установить новый тариф"
      :modal="true"
      class="w-[90vw] sm:w-[420px]"
      :closable="true"
    >
      <div class="flex flex-col gap-4 pt-2">
        <div class="flex flex-col gap-1">
          <label for="priceInput">Цена для клиента (₸)</label>
          <InputNumber
            id="priceInput"
            v-model="form.price"
            :min="1"
            placeholder="например, 4990"
            fluid
          />
        </div>
        <div class="flex flex-col gap-1">
          <label for="costInput">Себестоимость (₸)</label>
          <InputNumber id="costInput" v-model="form.cost" :min="0" placeholder="0" fluid />
        </div>
        <div class="flex flex-col gap-1">
          <label for="dateInput">Действует с</label>
          <InputText id="dateInput" v-model="form.dateBegin" type="date" />
        </div>
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button label="Отмена" text @click="showDialog = false" />
          <Button label="Сохранить" :loading="saving" @click="save" />
        </div>
      </template>
    </Dialog>
  </section>
</template>

<style scoped></style>
